<template>
  <div v-if="visible">
    <dx-popup
      :visible.sync="_visible"
      :width="getPopupWidth"
      :height="getPopupHeight"
      :show-title="true"
      :close-on-outside-click="true"
      :drag-enabled="false"
      :title="actionDialogTitle"
      :ref="popupName"
    >
      <!-- Load indicator -->
      <div v-show="loader.visible" class="load-indicator">
        <dx-load-indicator
          height="40"
          width="40"
        />
        <h4>{{actionDialogLoaderMessage}}</h4>
      </div>

      <!-- Page 1 -->
      <div v-show="page === 1 && !loader.visible">
        <div>
          <h4 class="no-margin">{{actionDialogBodyHeader}}</h4>
          <div v-html="actionDialogBodyDescription"></div>
          <div v-show="selection.duplicateRows.length">
            <h5 class="text-error duplicate-rows-header">
              Advarsel: Utvalget inneholder måler(e) med følgende duplikat:
            </h5>
            <div class="duplicate-rows-list">
              <div v-for="list in selection.duplicateRows">
                <h5 class="duplicate-rows-list-item-header">{{list[0].ean}} - {{list[0].subscriptionName}}</h5>
                <ul class="no-margin">
                  <li v-for="row in list" class="duplicate-rows-list-item-body">{{row.customerName}} - {{row.buildingName}} - {{row.subscriptionStatus}}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="action-dialog-wrapper">
          <div class="action-dialog-btn">
            <dx-button
              text="Avbryt"
              type="normal"
              styling-mode="contained"
              width="100%"
              @click="onActionDialogCancel"
            />
          </div>
          <div class="action-dialog-btn">
            <dx-button
              text="OK"
              type="default"
              styling-mode="contained"
              width="100%"
              @click="onActionDialogConfirm"
            />
          </div>
        </div>
      </div>

      <!-- Page 2 -->
      <div v-show="page === 2 && !loader.visible">
        <h4 class="no-margin">Varsle kunde om søknad på epost:</h4>
        <br>
        <div class="email-template-form">
          <div>
            <label>Til:</label>
            <input type="email" v-model="mail.to">
            <div class="email-template-form-error-message" v-show="mail.to.length && !isToValidFormInput">
              {{errorMessage('to')}}
            </div>
          </div>

          <div>
            <label>Avsender:</label>
            <input type="email" v-model="mail.replyTo">
            <div class="email-template-form-error-message" v-show="!isReplyToValidFormInput">
              {{errorMessage('replyTo')}}
            </div>
          </div>

          <div>
            <label>Bcc:</label>
            <input type="email" v-model="mail.bcc">
            <div class="email-template-form-error-message" v-show="!isBccValidFormInput">
              {{errorMessage('bcc')}}
            </div>
          </div>

          <div>
            <label>Emne:</label>
            <input type="text" v-model="mail.subject">
          </div>
        </div>

        <p class="no-margin">Forhåndsvisning av e-post:</p>

        <div class="email-template-preview-wrapper">
          <div class="email-template-preview">
            <trumbowyg
              :config="mail.templateConfig"
              v-model="_mailBody"
              class="form-control"
              name="content"
            >
            </trumbowyg>
          </div>

          <div class="action-dialog-wrapper">
            <div class="action-dialog-btn">
              <dx-button
                text="Lukk"
                type="normal"
                styling-mode="contained"
                width="100%"
                @click="onActionDialogCancel"
              />
            </div>

            <div class="action-dialog-btn">
              <dx-button
                text="Send e-post til kunde"
                type="default"
                styling-mode="contained"
                width="100%"
                :disabled="!isValidForm"
                @click="onActionDialogSendMail"
              />
            </div>
          </div>
        </div>
      </div>
    </dx-popup>
  </div>
</template>

<script>

  // External libs
  import DxPopup from 'devextreme-vue/popup';
  import DxButton from 'devextreme-vue/button';
  import DxLoadIndicator from 'devextreme-vue/load-indicator';

  import Trumbowyg from 'vue-trumbowyg';
  import 'trumbowyg/dist/ui/trumbowyg.css';

  // Services
  import MeterStatusService from '../services/elhub.meterstatus.service';

  const regExMailValidation = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g;
  const enumActionType = MeterStatusService.enumActionType;
  const defaultSubject = 'Forespørsel om tilgang til målerpunkt i Elhub';

  function getValidEmailAddressList(emailString) {
    const addressList = [...new Set(
      emailString.replace(/,/g, ';')
        .replace(/\s/g, '')
        .split(';')
    )];

    const validAddressList = addressList.filter(address => {
      const regEx = new RegExp(regExMailValidation);
      return regEx.exec(address) != null;
    });

    return addressList.length !== validAddressList.length ? null : validAddressList;
  }

  export default {
    name: 'ElhubPopupWizard',
    components: {
      DxPopup,
      DxButton,
      DxLoadIndicator,
      Trumbowyg
    },
    props: ['visible', 'type', 'selection', 'page'],
    data() {
      return {
        popupName: 'elhubPopup',
        mail: {
          to: '',
          replyTo: this.$user.email,
          bcc: this.$user.email,
          subject: defaultSubject,
          body: '',
          templateName: 'mailTemplateName',
          templateConfig: {
            btns: [
              ['viewHTML'],
              ['strong', 'em'],
              ['link'],
              ['fullscreen']
            ]
          }
        },
        loader: {
          visible: false
        }
      }
    },
    computed: {
      _page: {
        get() {
          return this.page != null ? this.page : 1;
        },
        set(value) {
          return this.$emit('update:page', value);
        }
      },
      _visible: {
        get() {
          return this.visible;
        },
        set(value) {
          this.$emit('update:visible', value);
        }
      },
      _mailBody: {
        get() {
          if (this.page !== 2) return '';
          if (this.mail.body === '') this.mail.body = this.mailTemplate();
          return this.mail.body;
        },
        set(value) {
          this.mail.body = value;
        }
      },
      popup() {
        return this.$refs[this.popupName].instance;
      },
      mailTemplateElem() {
        return this.$refs[this.mail.templateName];
      },
      actionDialogBodyDescription() {

        const requestAccessDescription = `
          <p>Ved å trykke på OK, sendes forespørsel om tredjepartstilgang til de valgte målepunkt inn til Elhub.</p>
          <ul>
            <li>Husk å ta med alle el abonnementsmålere, også profilmålte.</li>
            <li>Kunden får ingen automatisk beskjed fra Elhub om dette, så bruk funksjonen på neste side for å informere kunden på e-post.</li>
          </ul>
          <p></p>
          <p></p>
          <p>Etabler gjerne ferdig alle målerne før du sender dem, så blir arbeidet med godkjenning enklere for kunden.</p>
        `;

        const revokeAccessDescription = `
        <p>
          Ørn importerer måleverdier fra Elhub for to tjenester - Optima Energi og Fakturakontroll.
          <br>
          Avslutt målepunkt betyr at Ørn ikke vil motta verdier på måleren fra Elhub lenger.
        </p>
        <p>Før du sender Avslutt målepunkt til Elhub:</p>
        <ul>
          <li>vær sikker på at målepunktet ikke skal ha noen av disse tjenestene.</li>
          <li>vær sikker på at målepunktet ikke er en duplikat av en måler som Ørn fortsatt skal ha måleverdier på for en annen kunde, se duplikatliste under.</li>
          <li>når du trykker "OK" så vil dette avslutte alle eventuelle duplikater.</li>
        </ul>
        <p>Målepunkt skal avsluttes i Elhub dersom vi ikke har oppdrag for kunden lenger.</p>
        `;

        switch (this.type) {
          case enumActionType.REQUEST:
            return requestAccessDescription;
          case enumActionType.REVOKE:
            return revokeAccessDescription;
          default: return [];
        }
      },
      actionDialogTitle() {
        switch (this.type) {
          case enumActionType.REQUEST:
            if (this.page === 2) return 'Forespørsel er sendt!';
            return 'Bekreft søknad om tilgang';
          case enumActionType.REVOKE:
            return 'Bekreft fjerning av tilgang';
          case enumActionType.MAIL:
            return 'Send mail til kunde';
          default: return '';
        }
      },
      actionDialogBodyHeader() {
        switch (this.type) {
          case enumActionType.REQUEST:
            return `Send søknad om tilgang til ${this.selection.requestAccessRows.length} måler(e) i Elhub`;
          case enumActionType.REVOKE:
            return `Fjern tilgang til ${this.selection.revokeAccessRows.length} målere i Elhub`;
          default: return '';
        }
      },
      actionDialogLoaderMessage() {
        switch (this.page) {
          case 2:
            return 'Sender mail...';
          default:
            return 'Laster...';
        }
      },
      getPopupHeight() {
        const hasDuplicates = this.selection.duplicateRows.length;
        if (this.page === 2) return 700;
        switch (this.type) {
          case enumActionType.REQUEST: return hasDuplicates ? 590 : 500;
          case enumActionType.REVOKE: return hasDuplicates ? 650 : 500;
        }
      },
      getPopupWidth() {
        return this.page === 2 ? 850 : 650;
      },
      isToValidFormInput() {
        return getValidEmailAddressList(this.mail.to);
      },
      isReplyToValidFormInput() {
        return this.mail.replyTo ? getValidEmailAddressList(this.mail.replyTo) : true;
      },
      isBccValidFormInput() {
        return this.mail.bcc ? getValidEmailAddressList(this.mail.bcc) : true;
      },
      isValidForm() {
        return this.mail.to.length &&
          this.isToValidFormInput &&
          this.isReplyToValidFormInput &&
          this.isBccValidFormInput;
      }
    },
    methods: {
      getCurrentData() {
        switch (this.type) {
          case enumActionType.REQUEST:
            return this.selection.requestAccessRows;
          case enumActionType.REVOKE:
            return this.selection.revokeAccessRows;
          case enumActionType.MAIL:
            return this.selection.revokeAccessRows;
          default:
            return null;
        }
      },
      getMailData() {

        const toList  = getValidEmailAddressList(this.mail.to);
        const bccList = getValidEmailAddressList(this.mail.bcc);
        const replyToList = getValidEmailAddressList(this.mail.replyTo);

        if (!toList) {
          alert('Feil epost i "Til"-felt');
          return;
        }

        if (this.mail.bcc && !bccList) {
          alert('Feil epost i "Bcc"-felt');
          return;
        }

        if (this.mail.replyTo && !replyToList) {
          alert('Feil epost i "Avsender"-felt');
          return;
        }

        return {
          contentType:  'text/html; charset=UTF-8',
          to:           toList,
          bcc:          bccList,
          replyTo:      replyToList ? replyToList[0] : null,
          subject:      this.mail.subject,
          body:         `<html><body>${this.mail.body}</body></html>`
        };
      },
      getTableRowsHTML() {
        const data = this.getCurrentData();
        return data.reduce((acc, row, index) => {
          const isOdd = index % 2 !== 0;
          return acc +
            `<tr ${isOdd ? 'class="table-row-odd" bgcolor="#f2f2f2"': ''}>
              <td>${row.buildingName}</td>
              <td>${row.subscriptionName}</td>
              <td>${row.ean}</td>
            </tr>
            `;
        }, '');
      },
      mailTemplate() {
        const rowsHTML = this.getTableRowsHTML();
        const mailText = `
          <p>Hei.</p>
          <div>Vi har nå sendt forespørsel til Elhub om å få måleverdier på strømmålerne deres til bruk i Optima og/eller fakturakontroll.</div>
          <div style="margin-top: 10px;">Dere må godkjenne dette i Elhub, se oppskrift her:
            <a href="https://www.entro.no/elhub/">https://www.entro.no/elhub/</a>
          </div>
          <p>Vi har forespurt på disse målerne:</p>
          <table cellpadding="10">
            <thead>
              <th>BYGG</th>
              <th>ABONNEMENTSNAVN</th>
              <th>MÅLEPUNKT-ID</th>
            </thead>
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>
          <br>
          <p>Med vennlig hilsen</p>
          <p>
            <b>${this.$user.name.toUpperCase()}</b>
            <span>&nbsp; /&nbsp; Ørn Software AS&nbsp; /&nbsp;</span>
            <a href="https://www.ornsoftware.com/">
              <b style="color: blue;">ornsoftware.com</b>
            </a>
          </p>
          <style>
            table { border-collapse: collapse; }
            tbody tr td { padding: 10px 15px; }
            .table-row-odd { background-color: #f2f2f2;}
          </style>
          `;

        return mailText;
      },
      errorMessage(id) {
        switch (id) {
          case 'to': return 'Feil verdi i feltet "Til"';
          case 'replyTo': return 'Feil verdi i feltet "Avsender"';
          case 'bcc': return 'Feil verdi i feltet "Bcc"';
        }
      },
      resetForm() {
        this.mail.body    = '';
        this.mail.to      = '';
        this.mail.replyTo = this.$user.email;
        this.mail.bcc     = this.$user.email;
        this.mail.subject = defaultSubject;
      },
      onActionDialogConfirm() {
        this.loader.visible = true;
        this.$emit('confirm');
      },
      onActionDialogCancel() {
        this.$emit('cancel');
      },
      onActionDialogSendMail() {
        const mailData = this.getMailData();
        if (!mailData) return;
        this.loader.visible = true;
        this.$emit('send-mail', mailData);
      },
    },
    watch: {
      visible(status) {
        if (!status) {
          this._page = 1;
          this.resetForm();
          this.loader.visible = false;
        }
      },
      page() {
        this.loader.visible = false;
      }
    }
  };
</script>

<style scoped>

  .duplicate-rows-header {
    margin:        24px 0 0 0;
    border-bottom: 1px solid hsl(0, 0%, 88%);
  }

  .duplicate-rows-list {
    overflow-y:    auto;
    max-height:    215px;
    border-bottom: 1px solid hsl(0, 0%, 88%);
  }

  .duplicate-rows-list-item-header {
    margin: 3px 0 0 0;
  }

  .duplicate-rows-list-item-body {
    font-size: 11px;
  }

  .action-dialog-wrapper {
    position:         absolute;
    bottom:           15px;
    left:             0;
    width:            100%;
    padding:          0 20px;
    background-color: hsl(0, 0%, 100%);
  }

  .action-dialog-btn {
    display: inline-block;
    width:   50%
  }

  .action-dialog-btn + .action-dialog-btn {
    padding-left: 6px;
  }

  .email-template-preview-wrapper {
    border-bottom: 1px solid hsl(0, 0%, 84%);
    margin:        3px 0 0 0;
  }

  .email-template-preview {
    overflow: hidden;
    height:   339px;
  }

  .email-template-form {
    margin-bottom: 20px;
  }

  .email-template-form label {
    display:     inline-block;
    min-width:   100px;
    font-weight: 600;
  }

  .email-template-form input {
    padding:       6px;
    margin:        3px 0;
    border:        1px solid hsl(0, 0%, 89%);
    border-radius: 3px;
    width:         557px;
  }

  .email-template-form-error-message {
    margin-left:   102px;
    margin-bottom: 12px;
    color:         red;
    font-weight:   600;
    font-size:     12px;
  }

  .load-indicator {
    text-align: center;
    margin-top: 100px;
  }
</style>
<style>
  .email-template-preview p, .email-template-preview div {
    font-size: 13px;
  }

  .email-template-preview table thead th {
    font-size:  12px;
  }

  .email-template-preview table tr td {
    font-size:  12px;
  }

  .trumbowyg-box, .trumbowyg-editor {
    margin: 0;
  }

</style>
